export const SUPPORT_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/avif',
] as const;

export const SUPPORT_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
export const SUPPORT_ATTACHMENT_MAX_COUNT = 5;

export type SupportImageValidationResult =
  | { ok: true }
  | { ok: false; reason: string };

export function validateSupportImageFile(file: File): SupportImageValidationResult {
  if (!file || file.size <= 0) {
    return { ok: false, reason: 'Fichier vide.' };
  }
  if (file.size > SUPPORT_ATTACHMENT_MAX_BYTES) {
    return { ok: false, reason: 'Fichier trop volumineux (max 5 Mo).' };
  }
  const mime = String(file.type ?? '').toLowerCase();
  if (!SUPPORT_IMAGE_MIME_TYPES.includes(mime as (typeof SUPPORT_IMAGE_MIME_TYPES)[number])) {
    return { ok: false, reason: 'Type de fichier non autorisé (images uniquement).' };
  }
  return { ok: true };
}

export function validateSupportImageFileList(files: File[]): SupportImageValidationResult {
  if (files.length > SUPPORT_ATTACHMENT_MAX_COUNT) {
    return { ok: false, reason: `Maximum ${SUPPORT_ATTACHMENT_MAX_COUNT} pièces jointes.` };
  }
  for (const file of files) {
    const check = validateSupportImageFile(file);
    if (!check.ok) return check;
  }
  return { ok: true };
}

function fileFromBlob(blob: Blob, fileName: string): File {
  return new File([blob], fileName, { type: blob.type || 'image/png' });
}

/** ponytail: cross-origin images may render blank in DOM capture. */
export async function captureVisiblePage(target: HTMLElement = document.body): Promise<File | null> {
  if (typeof window === 'undefined') return null;
  try {
    const { toPng } = await import('html-to-image');
    const dataUrl = await toPng(target, {
      cacheBust: true,
      pixelRatio: Math.min(window.devicePixelRatio || 1, 2),
    });
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    return fileFromBlob(blob, `page-${Date.now()}.png`);
  } catch {
    return null;
  }
}

export async function captureScreen(): Promise<File | null> {
  if (typeof window === 'undefined' || !navigator.mediaDevices?.getDisplayMedia) return null;
  let stream: MediaStream | null = null;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
    const track = stream.getVideoTracks()[0];
    if (!track) return null;
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    await video.play();
    await new Promise<void>((resolve) => {
      if (video.readyState >= 2) resolve();
      else video.onloadeddata = () => resolve();
    });
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) return null;
    return fileFromBlob(blob, `screen-${Date.now()}.png`);
  } catch {
    return null;
  } finally {
    stream?.getTracks().forEach((t) => t.stop());
  }
}

export function readClipboardImageFromEvent(event: ClipboardEvent): File | null {
  const items = event.clipboardData?.items;
  if (!items?.length) return null;
  for (const item of Array.from(items)) {
    if (!item.type.startsWith('image/')) continue;
    const blob = item.getAsFile();
    if (!blob) continue;
    const ext = blob.type.split('/')[1] || 'png';
    return fileFromBlob(blob, `paste-${Date.now()}.${ext}`);
  }
  return null;
}

export async function readClipboardImage(): Promise<File | null> {
  if (typeof navigator === 'undefined' || !navigator.clipboard?.read) return null;
  try {
    const items = await navigator.clipboard.read();
    for (const item of items) {
      for (const type of item.types) {
        if (!type.startsWith('image/')) continue;
        const blob = await item.getType(type);
        const ext = type.split('/')[1] || 'png';
        return fileFromBlob(blob, `paste-${Date.now()}.${ext}`);
      }
    }
  } catch {
    return null;
  }
  return null;
}

export async function attachCrashScreenshotToTicket(input: {
  apiBase: string;
  ticketId: string;
  sentryEventId?: string | null;
  token?: string | null;
}): Promise<void> {
  const file = await captureVisiblePage();
  if (!file) return;
  const fd = new FormData();
  fd.append('files', file, file.name);
  if (input.sentryEventId) {
    fd.append('sentryEventId', input.sentryEventId);
  }
  try {
    await fetch(
      `${input.apiBase.replace(/\/$/, '')}/support/tickets/${encodeURIComponent(input.ticketId)}/attachments`,
      {
        method: 'POST',
        headers: input.token ? { Authorization: `Bearer ${input.token}` } : {},
        body: fd,
      },
    );
  } catch {
    // best effort
  }
}

export function appendSupportImageFiles(current: File[], incoming: File[]): File[] {
  const merged = [...current];
  for (const file of incoming) {
    if (merged.length >= SUPPORT_ATTACHMENT_MAX_COUNT) break;
    const check = validateSupportImageFile(file);
    if (!check.ok) continue;
    merged.push(file);
  }
  return merged;
}
